import React, { useEffect, useRef, useState } from "react";
import Map, { Source, Layer, NavigationControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import "./BestMap.css";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

/* ======================
   DATA MERGE
====================== */
const mergeData = (geojson, lookup, normalizeCountry) => {
  const matchedCountries = new Set();
  const dataCountries = Array.from(lookup.keys());
  
  geojson.features.forEach((f) => {
    const name = f.properties.ADMIN || f.properties.name;
    const key = normalizeCountry(name);
    const data = lookup.get(key);

    f.properties.countryName = name;
    f.properties.hasData = !!data;
    f.properties.fobPercent = data?.fobPercent || 0;
    f.properties.totalFob = data?.totalFob || 0;
    f.properties.totalFc = data?.totalFc || 0;
    f.properties.fcTotalsByType = data?.fcTotalsByType || [];
    f.properties.shipmentCount = data?.shipmentCount || 0;
    
    if (data) {
      matchedCountries.add(key);
    }
  });
  
  // Log countries in data but not found in map
  const unmatchedCountries = dataCountries.filter(c => !matchedCountries.has(c));
  if (unmatchedCountries.length > 0) {
    console.log('⚠️ Countries in data but NOT found on map:', unmatchedCountries);
  }
  console.log('✅ Countries matched on map:', Array.from(matchedCountries));
  
  return { geojson, matchedCountries };
};

/* ======================
   LABEL SOURCE (POLYGON BASED)
====================== */
const buildLabelSource = (geojson) => {
  const seen = new Set(); // track ki kaunse country add ho chuki
  const features = [];

  geojson.features.forEach((f) => {
    if (!f.properties.hasData) return;

    const country = f.properties.countryName;
    if (seen.has(country)) return; // skip if already added
    seen.add(country);

    // centroid calculate karte hai
    let coords = f.geometry.coordinates;
    let lon = 0, lat = 0, count = 0;

    const walk = (c) => {
      if (typeof c[0] === "number") {
        lon += c[0];
        lat += c[1];
        count++;
      } else c.forEach(walk);
    };

    walk(coords);

    lon /= count;
    lat /= count;

    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: [lon, lat] },
      properties: {
        name: country,
        percent: f.properties.fobPercent,
      },
    });
  });

  return { type: "FeatureCollection", features };
};

/* ======================
   UNMAPPED MARKERS (for countries not in GeoJSON)
====================== */
const COUNTRY_COORDINATES = {
  'HONG KONG': [114.1694, 22.3193],
  'MACAO': [113.5439, 22.1987],
  'MACAO SAR CHINA': [113.5439, 22.1987],
  'TAIWAN': [120.9605, 23.6978],
};

const buildUnmappedMarkers = (lookup, matchedCountries, normalizeCountry) => {
  const features = [];
  const dataCountries = Array.from(lookup.keys());
  
  dataCountries.forEach((countryKey) => {
    if (matchedCountries.has(countryKey)) return; // already on map
    
    const coords = COUNTRY_COORDINATES[countryKey];
    if (!coords) return; // no fallback coordinates
    
    const data = lookup.get(countryKey);
    
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: coords },
      properties: {
        name: countryKey,
        percent: data?.fobPercent || 0,
        totalFob: data?.totalFob || 0,
        totalFc: data?.totalFc || 0,
        fcTotalsByType: data?.fcTotalsByType || [],
        shipmentCount: data?.shipmentCount || 0,
        hasData: true
      },
    });
  });
  
  if (features.length > 0) {
    console.log('📍 Added unmapped country markers:', features.map(f => f.properties.name));
  }
  
  return { type: "FeatureCollection", features };
};

/* ======================
   FIT BOUNDS
====================== */
const getBounds = (geojson) => {
  let minLng = 180, minLat = 90, maxLng = -180, maxLat = -90;
  let ok = false;

  const walk = (c) => {
    if (typeof c[0] === "number") {
      minLng = Math.min(minLng, c[0]);
      minLat = Math.min(minLat, c[1]);
      maxLng = Math.max(maxLng, c[0]);
      maxLat = Math.max(maxLat, c[1]);
      ok = true;
    } else c.forEach(walk);
  };

  geojson.features.forEach((f) => {
    if (f.properties.hasData) walk(f.geometry.coordinates);
  });

  return ok ? [[minLng, minLat], [maxLng, maxLat]] : null;
};

/* ======================
   MAP LAYERS
====================== */
const baseLayer = (isDark) => ({
  id: "base",
  type: "fill",
  paint: {
    "fill-color": isDark ? "#1e293b" : "#f3f4f6",
    "fill-opacity": isDark ? 0.55 : 0.4,
  },
});

const dataLayer = {
  id: "data",
  type: "fill",
  filter: ["==", ["get", "hasData"], true],
  paint: {
    "fill-color": [
      "interpolate",
      ["linear"],
      ["get", "fobPercent"],
      0, "#dbeafe",
      10, "#bfdbfe",
      20, "#93c5fd",
      35, "#60a5fa",
      50, "#3b82f6",
      70, "#1e40af",
    ],
    "fill-opacity": 0.85,
  },
};

const borderLayer = (isDark) => ({
  id: "borders",
  type: "line",
  paint: {
    "line-color": [
      "case",
      ["==", ["get", "hasData"], true],
      isDark ? "#60a5fa" : "#1e3a8a",
      isDark ? "#334155" : "#cbd5e1",
    ],
    "line-width": [
      "case",
      ["==", ["get", "hasData"], true],
      1.5,
      0.6,
    ],
  },
});

/* ======================
   LABELS
====================== */
const labelLayer = (isDark) => ({
  id: "labels",
  type: "symbol",
  layout: {
    "symbol-placement": "point",
    "text-field": [
      "format",
      ["get", "name"],
      { "font-scale": 0.85 },
      "\n",
      {},
      ["concat", ["to-string", ["round", ["get", "percent"]]], "%"],
      { "font-scale": 1.05 },
    ],
    "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
    "text-size": [
      "interpolate",
      ["linear"],
      ["zoom"],
      1, 10,
      3, 12,
      5, 14
    ],
    "text-anchor": "center",
    "text-allow-overlap": false,
    "text-ignore-placement": false,
    "text-padding": 6,
  },
  paint: {
    "text-color": isDark ? "#e2e8f0" : "#0f172a",
    "text-halo-color": isDark ? "#0b1220" : "#ffffff",
    "text-halo-width": 1.4,
  },
});

/* ======================
   UNMAPPED MARKERS LAYERS
====================== */
const unmappedCircleLayer = (isDark) => ({
  id: "unmapped-circles",
  type: "circle",
  paint: {
    "circle-radius": [
      "interpolate",
      ["linear"],
      ["get", "percent"],
      0, 5,
      10, 7,
      30, 10,
      50, 13
    ],
    "circle-color": [
      "interpolate",
      ["linear"],
      ["get", "percent"],
      0, "#bfdbfe",
      10, "#93c5fd",
      20, "#60a5fa",
      35, "#3b82f6",
      50, "#1e40af",
    ],
    "circle-opacity": 0.9,
    "circle-stroke-color": isDark ? "#93c5fd" : "#1e3a8a",
    "circle-stroke-width": 1.5,
  },
});

const unmappedLabelLayer = (isDark) => ({
  id: "unmapped-labels",
  type: "symbol",
  layout: {
    "text-field": [
      "format",
      ["get", "name"],
      { "font-scale": 0.85 },
      "\n",
      {},
      ["concat", ["to-string", ["round", ["get", "percent"]]], "%"],
      { "font-scale": 1.05 },
    ],
    "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
    "text-size": 12,
    "text-anchor": "top",
    "text-offset": [0, 1.1],
    "text-allow-overlap": false,
    "text-ignore-placement": false,
    "text-padding": 6,
  },
  paint: {
    "text-color": isDark ? "#e2e8f0" : "#0f172a",
    "text-halo-color": isDark ? "#0b1220" : "#ffffff",
    "text-halo-width": 1.4,
  },
});


/* ======================
   MAIN COMPONENT
====================== */
const BestMap = ({
  worldGeoJSON,
  countryDataLookup,
  colorScale,
  normalizeCountry,
  formatCurrency,
  formatFcTotalsInline,
  setHoveredCountry,
  hoveredCountry,
  isDark = false,
}) => {
  const mapRef = useRef(null);
  const wrapRef = useRef(null);
  const fitted = useRef(false);

  const [geoData, setGeoData] = useState(null);
  const [labels, setLabels] = useState(null);
  const [unmappedMarkers, setUnmappedMarkers] = useState(null);
  const [hover, setHover] = useState(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!worldGeoJSON || !countryDataLookup) return;

    const { geojson: merged, matchedCountries } = mergeData(
      JSON.parse(JSON.stringify(worldGeoJSON)),
      countryDataLookup,
      normalizeCountry
    );

    setGeoData(merged);
    setLabels(buildLabelSource(merged));
    setUnmappedMarkers(buildUnmappedMarkers(countryDataLookup, matchedCountries, normalizeCountry));
  }, [worldGeoJSON, countryDataLookup]);

  useEffect(() => {
    if (geoData && mapRef.current && !fitted.current) {
      const b = getBounds(geoData);
      if (b) {
        mapRef.current.fitBounds(b, {
          padding: 90,
          maxZoom: 3,
          duration: 1200,
        });
        fitted.current = true;
      }
    }
  }, [geoData]);

  if (!MAPBOX_TOKEN) return <div>Mapbox token missing</div>;

  // The tooltip is measured against this box so it can flip rather than be
  // clipped by it.
  const W = wrapRef.current?.clientWidth || 0;
  const H = wrapRef.current?.clientHeight || 0;
  const TIP_W = 196;
  const TIP_H = 132;
  const tipLeft = mouse.x + 14 + TIP_W > W ? Math.max(8, mouse.x - 14 - TIP_W) : mouse.x + 14;
  const tipTop = mouse.y + 14 + TIP_H > H ? Math.max(8, mouse.y - 14 - TIP_H) : mouse.y + 14;

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%", height: "100%" }}>
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle={isDark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11'}
        projection="mercator"
        renderWorldCopies={false}
        attributionControl={false}
        logoPosition="bottom-left"
        maxBounds={[[-180, -85], [180, 85]]}
        initialViewState={{ longitude: 0, latitude: 20, zoom: 1.5 }}
        style={{ width: "100%", height: "100%" }}
        cooperativeGestures
        dragPan
        interactiveLayerIds={["data", "unmapped-circles"]}
        onMouseMove={(e) => {
          setMouse({ x: e.point.x, y: e.point.y });
          const f = e.features?.[0];
          setHover(f?.properties?.hasData ? f.properties : null);
        }}
        onMouseLeave={() => setHover(null)}
      >
        <NavigationControl position="top-right" showCompass={false} />
        {geoData && (
          <Source id="countries" type="geojson" data={geoData}>
            <Layer {...baseLayer(isDark)} />
            <Layer {...dataLayer} />
            <Layer {...borderLayer(isDark)} />
          </Source>
        )}

        {labels && (
          <Source key={JSON.stringify(labels)} id="labels" type="geojson" data={labels}>
            <Layer {...labelLayer(isDark)} />
          </Source>
        )}
        
        {unmappedMarkers && unmappedMarkers.features.length > 0 && (
          <Source id="unmapped" type="geojson" data={unmappedMarkers}>
            <Layer {...unmappedCircleLayer(isDark)} />
            <Layer {...unmappedLabelLayer(isDark)} />
          </Source>
        )}
      </Map>

      {/* 🔥 TOOLTIP */}
      {hover && (
        <div
          style={{
            position: "absolute",
            left: tipLeft,
            top: tipTop,
            width: TIP_W,
            background: isDark ? "#0f172a" : "#ffffff",
            color: isDark ? "#e2e8f0" : "#0f172a",
            border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
            padding: "10px 12px",
            borderRadius: "12px",
            fontSize: "12px",
            lineHeight: 1.6,
            boxShadow: isDark
              ? "0 14px 28px rgba(0,0,0,0.55)"
              : "0 14px 28px rgba(15,23,42,0.16)",
            pointerEvents: "none",
            zIndex: 20,
          }}
        >
          <div style={{
            fontWeight: 700,
            marginBottom: 6,
            paddingBottom: 6,
            borderBottom: `1px solid ${isDark ? "#1e293b" : "#f1f5f9"}`,
          }}>
            {hover.name || hover.countryName}
          </div>
          <div>Shipments: <b>{hover.shipmentCount}</b></div>
          <div>Percentage: <b>{((hover.percent || hover.fobPercent) || 0).toFixed(2)}%</b></div>
          <div>FOB: <b>{formatCurrency(hover.totalFob)}</b></div>
          {(() => {
            let fcData = hover.fcTotalsByType;
            if (typeof fcData === 'string') {
              try { fcData = JSON.parse(fcData); } catch { fcData = []; }
            }
            return formatFcTotalsInline && Array.isArray(fcData) && fcData.length > 0
              ? <div>FC: <b>{formatFcTotalsInline(fcData)}</b></div>
              : null;
          })()}
        </div>
      )}
    </div>
  );
};

export default BestMap;
